/**
 * React Query hooks for clause management.
 *
 * Provides hooks for fetching, creating, updating, and deleting clauses
 * within templates. Create/update/delete operations require admin role.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clausesApi } from '../services/api';
import { orgTemplateKeys } from './orgTemplateKeys';
import type { CreateClauseInput, UpdateClauseInput } from '../types';
import type { MigrateClauseInput } from '../types/orgTemplate';

/**
 * Fetch all clauses for a template. Without explicit pagination params the
 * hook drains every server page — consumers (wizard, review, clause admin)
 * act on the full clause set and must never see a truncated first page.
 */
export function useClauses(templateId: string, params?: { limit?: number }) {
  return useQuery({
    queryKey: ['clauses', templateId, params],
    queryFn: () =>
      params ? clausesApi.list(templateId, params) : clausesApi.listAll(templateId),
    enabled: !!templateId,
  });
}

/**
 * Fetch a single clause by ID.
 */
export function useClause(templateId: string, clauseId: string) {
  return useQuery({
    queryKey: ['clauses', templateId, clauseId],
    queryFn: () => clausesApi.get(templateId, clauseId),
    enabled: !!templateId && !!clauseId,
  });
}

/**
 * Create a new clause (admin only).
 */
export function useCreateClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, input }: { templateId: string; input: CreateClauseInput }) =>
      clausesApi.create(templateId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clauses', variables.templateId] });
    },
  });
}

/**
 * Update an existing clause (admin only).
 */
export function useUpdateClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      clauseId,
      input,
    }: {
      templateId: string;
      clauseId: string;
      input: UpdateClauseInput;
    }) => clausesApi.update(templateId, clauseId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clauses', variables.templateId] });
      queryClient.invalidateQueries({
        queryKey: ['clauses', variables.templateId, variables.clauseId],
      });
    },
  });
}

/**
 * Delete a clause (admin only).
 */
export function useDeleteClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, clauseId }: { templateId: string; clauseId: string }) =>
      clausesApi.delete(templateId, clauseId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clauses', variables.templateId] });
    },
  });
}

/**
 * Guided rename migration (admin only, org templates only — see
 * ClauseMigrationDialog): rewrites the active org template's Word tags to
 * match a clause and/or option identity change, activating a new template
 * version atomically with the clause rename. A migration moves the same
 * surfaces a version upload does — bodyTags, tagValidation, currentVersion —
 * so it invalidates the clause list, the org-template detail, and its
 * version list.
 */
export function useMigrateClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      clauseId,
      input,
    }: {
      templateId: string;
      clauseId: string;
      input: MigrateClauseInput;
    }) => clausesApi.migrate(templateId, clauseId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clauses', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(variables.templateId) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.versions(variables.templateId) });
    },
  });
}
